import { CdkMenuModule } from '@angular/cdk/menu'

import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  signal,
  SimpleChanges
} from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { MatRippleModule } from '@angular/material/core'
import { listAnimation } from '@core/animations'
import {
  PalantirAccordionComponent,
  PalantirBadgeComponent,
  PalantirButtonComponent,
  PalantirDropdownMenuModule,
  PalantirIconComponent,
  PalantirLayoutComponent,
  PalantirTextComponent,
  PalantirToolbarComponent
} from '@fieldcontrol/palantir'
import { Equipment, Warranty } from '@generated/sammathnaur.graphql'
import { TranslateModule } from '@ngx-translate/core'
import { MaintenanceWarrantyCardComponent } from './components/warranty-card/warranty-card.component'
import { MaintenanceWarrantyDetailComponent } from './components/warranty-detail/warranty-detail.component'
import { MaintenanceWarrantySkeletonComponent } from './components/warranty-skeleton/warranty-skeleton.component'
import { WarrantiesMapperService } from './services/warranties.mapper.service'
import { MaintenanceTabWarrantiesService } from './services/warranties.service'
import { WARRANTIES_TYPES, WARRANTY_TYPE } from './warranties.type'
import { MaintenancesService } from '../../core/api/maintenances/maintenances.service'

@Component({
    selector: 'maintenance-tab-warranties',
    templateUrl: './warranties.component.html',
    styleUrl: './warranties.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [listAnimation],
    imports: [
    CdkMenuModule,
    MaintenanceWarrantySkeletonComponent,
    MaintenanceWarrantyCardComponent,
    MaintenanceWarrantyDetailComponent,
    MatRippleModule,
    PalantirButtonComponent,
    PalantirToolbarComponent,
    PalantirDropdownMenuModule,
    PalantirTextComponent,
    PalantirIconComponent,
    PalantirAccordionComponent,
    PalantirBadgeComponent,
    PalantirLayoutComponent,
    PalantirDropdownMenuModule,
    ReactiveFormsModule,
    TranslateModule
],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA
    ]
})
export class WarrantiesMaintenanceTabComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ transform: booleanAttribute })
  public readonly firstView?: boolean = false

  @Input()
  public readonly maintenanceId?: string

  @Input()
  public readonly maintenanceTypeId?: string

  @Input()
  public readonly companyId?: string

  @Input()
  public readonly equipments?: Equipment[] = []

  @Input({ transform: booleanAttribute })
  public readonly hasCreateWarrantyPermission: boolean = false

  @Input({ transform: booleanAttribute })
  public readonly hasEditWarrantyPermission: boolean = false

  public warrantiesTypes = WARRANTIES_TYPES
  public WARRANTY_TYPE = WARRANTY_TYPE

  public editingIndex = signal<number | null>(null)

  private readonly resetWarrantiesServiceVariablesBound = this.resetWarrantiesServiceVariables.bind(this)

  public constructor(
    public readonly maintenanceTabWarrantiesService: MaintenanceTabWarrantiesService,
    private readonly maintenancesService: MaintenancesService,
    public readonly warrantiesMapperService: WarrantiesMapperService
  ) { }

  public async ngOnInit(): Promise<void> {
    await this.init()
    window.addEventListener('clearMaintenanceWarrantiesVariables', this.resetWarrantiesServiceVariablesBound)
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['hasCreateWarrantyPermission'] || changes['hasEditWarrantyPermission']) {
      await this.syncPermissionsWithService()
      return
    }

    if (changes['maintenanceId'] || changes['equipments']) {
      await this.init()
    }
  }

  public ngOnDestroy(): void {
    window.removeEventListener('clearMaintenanceWarrantiesVariables', this.resetWarrantiesServiceVariablesBound)
  }

  private async init(): Promise<void> {
    await this.syncPermissionsWithService()
    if (!this.maintenanceTabWarrantiesService.hasData()) {
      this.maintenanceTabWarrantiesService.isLoading.set(true)
      await this.loadMaintenanceEquipments()
      await this.loadMaintenanceWarranties()
      this.maintenanceTabWarrantiesService.isLoading.set(false)
    }

    if (this.maintenanceId) {
      this.maintenanceTabWarrantiesService.maintenanceId.set(this.maintenanceId)
    }

    this.maintenanceTabWarrantiesService.setHasWarranties()
    if (!this.maintenanceTabWarrantiesService.selectedWarrantyForm) {
      const type = this.maintenanceTabWarrantiesService.selectedWarrantyType()
      const firstWarranty = this.maintenanceTabWarrantiesService.getFirstWarranty(type)
      if (firstWarranty) {
        this.maintenanceTabWarrantiesService.selectWarranty(firstWarranty, 0)
      }
    }
  }

  public refresh(): void {
    console.log('oi')
    this.maintenanceTabWarrantiesService.isLoading.set(true)
  }

  public async changeWarrantyType(type: WARRANTY_TYPE): Promise<void> {
    this.maintenanceTabWarrantiesService.selectedWarrantyType.set(type)
    this.maintenanceTabWarrantiesService.selectedWarrantyForm = undefined
    const firstWarranty = this.maintenanceTabWarrantiesService.getFirstWarranty(type)

    if (firstWarranty) {
      this.maintenanceTabWarrantiesService.selectWarranty(firstWarranty, 0)
    }
  }

  private async syncPermissionsWithService(): Promise<void> {
    this.maintenanceTabWarrantiesService.hasCreateWarrantyPermission.set(this.hasCreateWarrantyPermission)
    this.maintenanceTabWarrantiesService.hasEditWarrantyPermission.set(this.hasEditWarrantyPermission)
  }

  private async loadMaintenanceEquipments(): Promise<void> {
    if (!this.maintenanceId && !this.equipments?.length) {
      this.maintenanceTabWarrantiesService.equipments.set([])
      this.maintenanceTabWarrantiesService.isLoading.set(false)
      return
    }

    const equipments = this.equipments?.length
      ? this.equipments
      : await this.fetchMaintenanceEquipments()

    if (!equipments) return

    this.maintenanceTabWarrantiesService.equipments.set(equipments)
    this.maintenanceTabWarrantiesService.isLoading.set(false)
  }

  private async fetchMaintenanceEquipments(): Promise<Equipment[] | null> {
    if (!this.maintenanceId) return null

    const response = await this.maintenancesService.getMaintenanceWithWarranties(this.maintenanceId)
    return response.data?.maintenance?.equipments as Equipment[] || []
  }

  private async loadMaintenanceWarranties(): Promise<void> {
    if (!this.maintenanceId) return
    const warranties = (await this.maintenancesService.getMaintenanceWithWarranties(this.maintenanceId)).data?.maintenance?.warranties as Warranty[]
    if (!warranties) return

    const warrantiesForm = warranties.map((warranty) => {
      return this.warrantiesMapperService.createForm(warranty)
    })

    this.maintenanceTabWarrantiesService.warrantiesForm.set(warrantiesForm)

    this.maintenanceTabWarrantiesService.isLoading.set(false)
  }

  public async openDialog(): Promise<void> {
    this.maintenanceTabWarrantiesService.selectedMaintenanceTypeId.set(this.maintenanceTypeId)
    this.maintenanceTabWarrantiesService.selectedCompanyId.set(this.companyId)
    await this.maintenanceTabWarrantiesService.openDialog()
  }

  private resetWarrantiesServiceVariables(): void {
    this.maintenanceTabWarrantiesService.warrantiesForm.set([])
    this.maintenanceTabWarrantiesService.equipments.set([])
    this.maintenanceTabWarrantiesService.selectedWarrantyForm = undefined
    this.maintenanceTabWarrantiesService.selectedWarrantyFormIndex.set(null)
    this.maintenanceTabWarrantiesService.eventRequests = []
  }
}
